import { ChevronDown, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminApi } from "@/api";
import {
  Banner,
  Button,
  Card,
  EmptyState,
  SelectField,
  TextField,
} from "@/components/ui";
import type {
  EngagementEnvironment,
  HomeFeedLogDetail,
  HomeFeedLogItem,
  HomeFeedSummaryRow,
} from "@/types";
import { HomeFeedFunnel } from "./HomeFeedFunnel";
import { HomeFeedModal } from "./HomeFeedModal";
import { HomeFeedRow } from "./HomeFeedRow";
import { HOME_FEED_TABLE_COLUMNS } from "./homeFeedTable";

const PAGE_SIZE = 30;
const ENVIRONMENTS: EngagementEnvironment[] = ["dev", "prod"];

type Cursors = Record<EngagementEnvironment, string | null>;

function rowKey(item: HomeFeedLogItem) {
  return `${item._environment}:${item.home_feed_item_id}`;
}

function sortItems(items: HomeFeedLogItem[]) {
  return [...items].sort((a, b) => b.generated_at.localeCompare(a.generated_at));
}

function dedupe(items: HomeFeedLogItem[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = rowKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Home Feed section: funnel on top, item log below.
 *
 * The funnel is the entry point — it says *where* the drop-off is; the list
 * says *which* cards. `proactive_message` items are not here: those are sends,
 * not cards, and live under Proactive Messages.
 */
export function HomeFeedPage({
  apiBaseUrl,
  bearerToken,
  samsApiBaseUrl,
}: {
  apiBaseUrl: string;
  bearerToken: string;
  samsApiBaseUrl?: string;
}) {
  const api = useMemo(
    () => AdminApi.forSamsLogin(apiBaseUrl, bearerToken, apiBaseUrl, samsApiBaseUrl || undefined),
    [apiBaseUrl, bearerToken, samsApiBaseUrl],
  );

  const [summary, setSummary] = useState<HomeFeedSummaryRow[]>([]);
  const [items, setItems] = useState<HomeFeedLogItem[]>([]);
  const [cursors, setCursors] = useState<Cursors>({ dev: null, prod: null });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState<string[]>([]);

  const [type, setType] = useState("all");
  const [email, setEmail] = useState("");
  const [sinceDays, setSinceDays] = useState("14");

  const [selectedKey, setSelectedKey] = useState("");
  const [detail, setDetail] = useState<HomeFeedLogDetail | undefined>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const selected = items.find((item) => rowKey(item) === selectedKey);
  const hasMore = Boolean(cursors.dev || cursors.prod);

  const query = useMemo(
    () => ({
      type: type as "all" | "suggestion" | "reply_reminder",
      email: email.trim() || undefined,
      sinceDays: Number(sinceDays) || 14,
    }),
    [type, email, sinceDays],
  );

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // Prod only for an admin with prod access (SAMS says which).
      const environments = (await api.readableEnvironments()).filter((e) => ENVIRONMENTS.includes(e));
      const [lists, summaries] = await Promise.all([
        Promise.all(
          environments.map((environment) =>
            api
              .listHomeFeedItems({ environment, limit: PAGE_SIZE, ...query })
              .then((page) => ({ environment, page })),
          ),
        ),
        Promise.all(
          environments.map((environment) =>
            api.homeFeedSummary({
              environment,
              sinceDays: query.sinceDays,
              type: query.type,
            }),
          ),
        ),
      ]);

      setItems(sortItems(lists.flatMap((l) => l.page.items || [])));
      setCursors({
        dev: lists.find((l) => l.environment === "dev")?.page.next_cursor ?? null,
        prod: lists.find((l) => l.environment === "prod")?.page.next_cursor ?? null,
      });
      setSummary(mergeSummaries(summaries.flatMap((s) => s.by_type || [])));
      setNotes(
        [
          ...lists.map((l) => l.page.load_error),
          ...summaries.map((s) => s.load_error),
        ].filter(Boolean) as string[],
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    const pending = ENVIRONMENTS.filter((environment) => cursors[environment]);
    if (pending.length === 0) return;
    setLoadingMore(true);
    setError("");
    try {
      const pages = await Promise.all(
        pending.map((environment) =>
          api
            .listHomeFeedItems({
              environment,
              limit: PAGE_SIZE,
              cursor: cursors[environment],
              ...query,
            })
            .then((page) => ({ environment, page })),
        ),
      );
      setItems((current) =>
        sortItems(dedupe([...current, ...pages.flatMap((p) => p.page.items || [])])),
      );
      setCursors((current) => {
        const next = { ...current };
        for (const p of pages) next[p.environment] = p.page.next_cursor ?? null;
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (item: HomeFeedLogItem) => {
    setSelectedKey(rowKey(item));
    setDetail(undefined);
    setDetailError("");
    setDetailLoading(true);
    try {
      setDetail(
        await api.getHomeFeedItemDetail(item._environment, item.home_feed_item_id),
      );
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : String(err));
    } finally {
      setDetailLoading(false);
    }
  };

  // Title and description live in the shell's page header (pageMeta).
  return (
    <div className="grid gap-5">
      <Banner tone="success">
        <strong>노출</strong>은 클라이언트 보고라 앱이 종료되면 유실될 수 있어 실제보다
        낮게 나올 수 있습니다. <strong>추정</strong> 배지는 전송 이벤트에 메시지 ID가 없어
        시간 기반으로 연결한 값입니다. <strong>일정(schedule) 카드는 추적되지 않습니다.</strong>{" "}
        생성 프롬프트는 서버 코드에 있어 여기서 편집할 수 없습니다 — 개선이 필요하면
        상세에서 <strong>사례 복사</strong>로 개발팀에 전달해 주세요.
      </Banner>

      {error && <Banner tone="error">{error}</Banner>}
      {notes.map((note) => (
        <Banner key={note} tone="error">
          {note} (다른 환경 결과는 그대로 표시됩니다)
        </Banner>
      ))}

      <Card className="grid gap-3 p-4 md:grid-cols-[180px_1fr_140px_auto]">
        <SelectField
          label="타입"
          value={type}
          onChange={setType}
          options={["all", "suggestion", "reply_reminder"]}
        />
        <TextField
          label="사용자 이메일"
          value={email}
          onChange={setEmail}
          placeholder="비우면 전체"
        />
        <SelectField
          label="기간(일)"
          value={sinceDays}
          onChange={setSinceDays}
          options={["7", "14", "30", "90"]}
        />
        <Button className="self-end" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          조회
        </Button>
      </Card>

      <HomeFeedFunnel rows={summary} />

      {items.length > 0 && (
        <div
          className={`${HOME_FEED_TABLE_COLUMNS} px-4.5 text-xs font-medium text-muted-foreground`}
        >
          <span>생성시각</span>
          <span>타입</span>
          <span>사용자</span>
          <span>세션</span>
          <span>내용</span>
          <span>노출</span>
          <span>반응</span>
          <span>이후 대화</span>
          <span>env</span>
        </div>
      )}

      <div className="grid gap-2">
        {items.map((item) => (
          <HomeFeedRow
            key={rowKey(item)}
            item={item}
            selected={rowKey(item) === selectedKey}
            onSelect={() => void openDetail(item)}
          />
        ))}
      </div>

      {!loading && items.length === 0 && (
        <EmptyState
          title="홈 피드 항목이 없습니다"
          body="기간을 늘리거나 필터를 비워보세요. prod DB가 설정되지 않은 환경에서는 dev 결과만 표시됩니다."
        />
      )}

      {hasMore && (
        <div className="flex justify-center pt-2 pb-4.5">
          <Button
            onClick={loadMore}
            disabled={loading || loadingMore}
            variant="outline"
          >
            {loadingMore ? <Loader2 className="animate-spin" /> : <ChevronDown />}
            Load more
          </Button>
        </div>
      )}

      <HomeFeedModal
        item={selected}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        open={Boolean(selected)}
        onClose={() => setSelectedKey("")}
      />
    </div>
  );
}

/**
 * Fold the dev and prod funnels into one row per type.
 *
 * Rates are recomputed from the summed counts rather than averaged — averaging
 * two ratios would weight a tiny dev sample the same as prod.
 */
function mergeSummaries(rows: HomeFeedSummaryRow[]): HomeFeedSummaryRow[] {
  const byType = new Map<string, HomeFeedSummaryRow>();
  for (const row of rows) {
    const current = byType.get(row.type);
    if (!current) {
      byType.set(row.type, { ...row });
      continue;
    }
    current.generated += row.generated;
    current.impressed += row.impressed;
    current.opened += row.opened;
    current.converted += row.converted;
  }
  const rate = (numerator: number, denominator: number) =>
    denominator > 0 ? Number((numerator / denominator).toFixed(4)) : null;
  return [...byType.values()].map((row) => ({
    ...row,
    impression_rate: rate(row.impressed, row.generated),
    open_rate: rate(row.opened, row.impressed),
    conversion_rate: rate(row.converted, row.opened),
  }));
}
